import React, { useContext } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Badge, Tooltip, Avatar, Tag, Popover } from "antd";
import {
  ShopOutlined, UserOutlined, QuestionCircleOutlined,
  GlobalOutlined, ClockCircleOutlined, StarFilled, StarOutlined,
} from "@ant-design/icons";
import { API_DOMAIN } from "../constants";
import { AuthContext } from "../contexts/AuthContext";
import { TICKET_STATUS } from "./constants";
import { expiryDisplay, getCardCountry, fmtShortDate } from "./helpers";
import { PIPELINE_COLUMNS } from "./pipeline";
import { AgentDropdown } from "./AgentDropdown";

export const ActiveChatCard = React.memo(function ActiveChatCard({ chat, isSelected, onClick, onChatPatch, matchingQueries = [], onQueryJump }) {
  const { authState } = useContext(AuthContext);
  const customerType = chat?.customer_type;
  const avatarIcon = customerType === 0 ? <ShopOutlined /> : customerType === 1 ? <UserOutlined /> : <QuestionCircleOutlined />;
  const avatarColor = customerType === 0 ? "#1677ff" : customerType === 1 ? "#722ed1" : "#d48806";
  const typeLabel = customerType === 0 ? "B2B" : customerType === 1 ? "B2C" : null;
  const typeColor = customerType === 0 ? "blue" : "purple";
  const tsCfg = chat.ticket_status != null ? TICKET_STATUS[chat.ticket_status] : null;
  const expiry = expiryDisplay(chat.expiry);
  const cardCountry = getCardCountry(chat.phone);
  const isIntl = cardCountry && cardCountry.name !== "United Arab Emirates";
  const timeStr = chat.last_message_time?.isValid?.()
    ? (chat.last_message_time.isSame(dayjs(), "day") ? chat.last_message_time.format("hh:mm A") : fmtShortDate(chat.last_message_time))
    : null;
  const flagEmoji = isIntl && cardCountry?.code
    ? cardCountry.code.toUpperCase().split("").map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)).join("")
    : null;

  const toggleStar = async (e) => {
    e.stopPropagation();
    const newStarred = !chat.starred;
    onChatPatch?.({ starred: newStarred });
    try {
      const res = await axios.post(
        API_DOMAIN + "/wati_messages/toggle_star_chat",
        { conversationId: chat.conversationId, userId: authState.id },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data?.success) throw new Error();
    } catch {
      onChatPatch?.({ starred: !newStarred });
    }
  };

  return (
    <div onClick={onClick} style={{
      padding: "9px 11px", borderRadius: 8,
      border: `1.5px solid ${isSelected ? "#1677ff" : "#d0d0d0"}`,
      borderLeft: `4px solid ${isSelected ? "#1677ff" : tsCfg?.border || "#d0d0d0"}`,
      backgroundColor: isSelected ? "#e6f4ff" : "#fff",
      cursor: "pointer", display: "flex", gap: 10, alignItems: "flex-start",
      boxShadow: isSelected ? "0 0 0 2px #bae0ff" : "0 1px 3px rgba(0,0,0,0.06)",
      transition: "all 0.15s",
      position: "relative",
    }}>
      {isIntl && (
        <GlobalOutlined style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: 72, color: "rgba(22,119,255,0.14)", pointerEvents: "none", zIndex: 0 }} />
      )}
      <div style={{ position: "relative", zIndex: 1, display: "flex", gap: 10, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <div style={{ position: "relative" }}>
            <Avatar size={38} icon={avatarIcon} style={{ backgroundColor: avatarColor, fontSize: 17 }} />
            {chat.unread_count > 0 && (
              <Badge count={chat.unread_count} style={{ backgroundColor: "#25d366", fontSize: 11, minWidth: 20, height: 20, lineHeight: "20px", padding: "0 5px", position: "absolute", top: -5, right: -8 }} />
            )}
          </div>
          <Tooltip title={chat.starred ? "Unstar" : "Star"}>
            <span onClick={toggleStar} style={{ cursor: "pointer", color: chat.starred ? "#faad14" : "#d9d9d9", fontSize: 13, lineHeight: 1, display: "flex" }}>
              {chat.starred ? <StarFilled /> : <StarOutlined />}
            </span>
          </Tooltip>
          {matchingQueries.length === 1 && (
            <Tooltip title={`#${matchingQueries[0].query_id} — scroll to card`}>
              <span
                onClick={e => { e.stopPropagation(); onQueryJump?.(matchingQueries[0].query_id); }}
                style={{
                  cursor: "pointer", userSelect: "none",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  minWidth: 18, height: 16, padding: "0 4px",
                  borderRadius: 8, fontSize: 10, fontWeight: 700,
                  background: "#e6f4ff", color: "#1677ff",
                  border: "1.5px solid #91caff",
                }}
              >
                1
              </span>
            </Tooltip>
          )}
          {matchingQueries.length > 1 && (
            <Popover
              trigger="click"
              placement="right"
              getPopupContainer={() => document.body}
              title={<span style={{ fontSize: 12 }}>Query Cards ({matchingQueries.length})</span>}
              content={
                <div style={{ minWidth: 200, maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                  {matchingQueries.map(q => {
                    const col = PIPELINE_COLUMNS.find(c => !c.isChat && !c.placeholder && c.statuses.includes(q.status));
                    return (
                      <div
                        key={q.query_id}
                        onClick={e => { e.stopPropagation(); onQueryJump?.(q.query_id); }}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 6, cursor: "pointer", transition: "background 0.12s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f0f0f0"}
                        onMouseLeave={e => e.currentTarget.style.background = ""}
                      >
                        <span style={{ fontWeight: 700, color: "#1677ff", fontSize: 12, flexShrink: 0 }}>#{q.query_id}</span>
                        {col && <span style={{ fontSize: 11, color: col.color, fontWeight: 600, flexShrink: 0 }}>{col.label}</span>}
                        {q.status && <span style={{ fontSize: 10, color: "#8c8c8c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.status}</span>}
                      </div>
                    );
                  })}
                </div>
              }
            >
              <span
                onClick={e => e.stopPropagation()}
                style={{
                  cursor: "pointer", userSelect: "none",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  minWidth: 18, height: 16, padding: "0 4px",
                  borderRadius: 8, fontSize: 10, fontWeight: 700,
                  background: "#e6f4ff", color: "#1677ff",
                  border: "1.5px solid #91caff",
                }}
              >
                {matchingQueries.length}
              </span>
            </Popover>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4, marginBottom: 2 }}>
            <Tooltip title={chat.customer_name || chat.phone || "Unknown"} placement="top" mouseEnterDelay={0.6}>
              <span style={{ fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {chat.customer_name || chat.phone || "Unknown"}
              </span>
            </Tooltip>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, gap: 2 }}>
              {timeStr && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                  {flagEmoji && (
                    <Tooltip title={cardCountry.name} placement="top">
                      <span style={{ fontSize: 13, lineHeight: 1, cursor: "default" }}>{flagEmoji}</span>
                    </Tooltip>
                  )}
                  <span style={{ fontSize: 10, color: "#8c8c8c", whiteSpace: "nowrap" }}>{timeStr}</span>
                </span>
              )}
              {expiry && (
                <Tooltip title="Conversation window expiry">
                  <span style={{ fontSize: 9, color: expiry.color, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 2 }}>
                    <ClockCircleOutlined style={{ fontSize: 9 }} />{expiry.label}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          <Tooltip title={chat.last_message || null} placement="bottom" mouseEnterDelay={0.5}>
            <div style={{ fontSize: 11, color: "#595959", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 5 }}>
              {chat.last_message || <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>No messages</span>}
            </div>
          </Tooltip>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, flex: 1 }}>
              {chat.agent && (
                <AgentDropdown chat={chat} compact isDark={false} onAssigned={fields => onChatPatch?.(fields)} />
              )}
              {chat.topic && chat.topic.toLowerCase() !== "general enquiry" && (
                <Tag style={{ fontSize: 9, margin: 0, padding: "0 4px", lineHeight: "16px", border: "1px solid #d9d9d9", color: "#595959", background: "#fafafa", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 90, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" }}>
                  {chat.topic}
                </Tag>
              )}
              {chat.ticket_type?.toLowerCase() === "ctwa" && (
                <Tag style={{ fontSize: 9, margin: 0, padding: "0 4px", lineHeight: "16px", flexShrink: 0, color: "#531dab", background: "#f9f0ff", border: "1px solid #d3adf7", fontWeight: 600, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" }}>
                  CTWA
                </Tag>
              )}
              {typeLabel && <Tag color={typeColor} style={{ fontSize: 9, margin: 0, padding: "0 4px", lineHeight: "16px", flexShrink: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" }}>{typeLabel}</Tag>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              {tsCfg && (
                <Tag style={{ fontSize: 9, margin: 0, padding: "0 5px", lineHeight: "16px", flexShrink: 0, color: tsCfg.color, background: tsCfg.bg, border: `1px solid ${tsCfg.border}`, fontWeight: 600, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" }}>
                  {tsCfg.label}
                </Tag>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}, (prev, next) =>
  prev.chat === next.chat &&
  prev.isSelected === next.isSelected &&
  prev.matchingQueries === next.matchingQueries
);
